import { Component, inject, signal, computed } from "@angular/core";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { DataService, Tour } from "./data";
import { Icon } from "./icon";
import { Reveal } from "./reveal";
import { IslandMap } from "./island-map";

@Component({
  selector: "app-tour-detail",
  imports: [Icon, RouterLink, Reveal, IslandMap],
  template: `
    @if (tour(); as t) {
      <div class="bg-paper min-h-screen pb-24">
        <!-- Hero -->
        <div class="relative h-[65vh] min-h-[520px] overflow-hidden">
          <img
            [src]="t.image"
            alt=""
            class="absolute inset-0 w-full h-full object-cover animate-pulse-slow"
            referrerpolicy="no-referrer"
          />
          <div
            class="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/10"
          ></div>

          <div
            class="container relative h-full flex flex-col justify-end pb-14 text-paper"
          >
            <nav
              class="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold text-paper/50 mb-8"
            >
              <a routerLink="/" class="hover:text-saffron transition-colors"
                >Home</a
              >
              <app-icon name="chevron-right" [size]="11" />
              <a
                routerLink="/tours"
                class="hover:text-saffron transition-colors"
                >Tours</a
              >
              <app-icon name="chevron-right" [size]="11" />
              <span class="text-paper">{{ t.title }}</span>
            </nav>

            <div
              class="flex flex-col md:flex-row md:items-end justify-between gap-8"
            >
              <div class="max-w-3xl">
                <div class="flex flex-wrap items-center gap-3 mb-5">
                  <span
                    class="route-code text-[11px] bg-saffron text-ink px-3 py-1.5 rounded-full"
                    >{{ routeLabel() }}</span
                  >
                  <span class="flex items-center gap-1.5 text-sm">
                    <app-icon name="star" [size]="14" class="text-saffron" />
                    {{ t.rating }}
                    <span class="text-paper/50">({{ t.reviews }} reviews)</span>
                  </span>
                </div>
                <h1
                  class="text-5xl md:text-7xl font-serif leading-[0.95] tracking-tight mb-4"
                >
                  {{ t.title }}
                </h1>
                <p class="text-paper/70 text-lg max-w-xl">{{ t.tagline }}</p>
              </div>

              <div class="flex items-center gap-3">
                <button
                  type="button"
                  aria-label="Share this tour"
                  (click)="share()"
                  class="w-12 h-12 rounded-full glass-ink flex items-center justify-center hover:text-saffron transition-all"
                >
                  <app-icon name="share" [size]="18" />
                </button>
                <button
                  type="button"
                  aria-label="Save this tour"
                  (click)="saved.set(!saved())"
                  class="w-12 h-12 rounded-full flex items-center justify-center transition-all"
                  [class]="
                    saved()
                      ? 'bg-vermilion text-paper glow-vermilion'
                      : 'glass-ink hover:text-vermilion'
                  "
                >
                  <app-icon
                    [name]="saved() ? 'heart' : 'heart-outline'"
                    [size]="18"
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="container mt-16 grid grid-cols-1 lg:grid-cols-3 gap-16">
          <!-- Main -->
          <div class="lg:col-span-2 space-y-14">
            <!-- Quick stats -->
            <div
              class="grid grid-cols-1 sm:grid-cols-3 gap-6 p-8 bg-paper-soft rounded-[1.5rem]"
              appReveal
            >
              <div class="space-y-1.5">
                <span
                  class="text-[10px] uppercase tracking-widest font-bold text-ink/40 block"
                  >Duration</span
                >
                <div class="flex items-center gap-2 font-semibold">
                  <app-icon name="clock" [size]="16" class="text-vermilion" />{{
                    t.duration
                  }}
                </div>
              </div>
              <div class="space-y-1.5">
                <span
                  class="text-[10px] uppercase tracking-widest font-bold text-ink/40 block"
                  >Pace</span
                >
                <div class="flex items-center gap-2 font-semibold">
                  <app-icon
                    name="compass"
                    [size]="16"
                    class="text-vermilion"
                  />{{ t.difficulty }}
                </div>
              </div>
              <div class="space-y-1.5">
                <span
                  class="text-[10px] uppercase tracking-widest font-bold text-ink/40 block"
                  >Best Season</span
                >
                <div class="flex items-center gap-2 font-semibold">
                  <app-icon name="globe" [size]="16" class="text-vermilion" />{{
                    t.bestSeason
                  }}
                </div>
              </div>
            </div>

            <!-- Tabs -->
            <div class="space-y-10">
              <div
                class="flex flex-wrap items-center gap-1 border-b border-sand"
              >
                @for (tab of tabs; track tab.id) {
                  <button
                    type="button"
                    (click)="activeTab.set(tab.id)"
                    class="relative px-5 py-3 text-[11px] uppercase tracking-widest font-bold transition-colors"
                    [class]="
                      activeTab() === tab.id
                        ? 'text-vermilion'
                        : 'text-ink/40 hover:text-ink'
                    "
                  >
                    {{ tab.label }}
                    @if (activeTab() === tab.id) {
                      <span
                        class="absolute left-0 right-0 -bottom-px h-[2px] bg-vermilion"
                      ></span>
                    }
                  </button>
                }
              </div>

              @if (activeTab() === "overview") {
                <div class="space-y-12">
                  <div class="space-y-5">
                    <h2 class="text-3xl font-serif">
                      The
                      <span class="italic font-light text-ink/40"
                        >Experience</span
                      >
                    </h2>
                    <p class="text-ink/65 text-lg leading-relaxed">
                      {{ t.description }}
                    </p>
                  </div>

                  <div class="space-y-4">
                    <h3
                      class="text-xs uppercase tracking-widest font-bold text-teal"
                    >
                      Highlights
                    </h3>
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      @for (h of t.highlights; track h) {
                        <div class="flex items-start gap-3 text-ink/70 text-sm">
                          <app-icon
                            name="check"
                            [size]="14"
                            class="text-teal mt-1 shrink-0"
                          />
                          {{ h }}
                        </div>
                      }
                    </div>
                  </div>

                  <div
                    class="grid grid-cols-1 md:grid-cols-2 gap-12 pt-10 border-t border-sand"
                  >
                    <div class="space-y-4">
                      <h3
                        class="text-xs uppercase tracking-widest font-bold text-teal"
                      >
                        Included
                      </h3>
                      @for (inc of t.included; track inc) {
                        <div
                          class="flex items-center gap-3 text-ink/70 text-sm"
                        >
                          <div
                            class="w-5 h-5 flex items-center justify-center bg-teal/15 shrink-0"
                          >
                            <app-icon
                              name="check"
                              [size]="11"
                              class="text-teal"
                            />
                          </div>
                          {{ inc }}
                        </div>
                      }
                    </div>
                    <div class="space-y-4">
                      <h3
                        class="text-xs uppercase tracking-widest font-bold text-ink/40"
                      >
                        Not Included
                      </h3>
                      @for (exc of t.excluded; track exc) {
                        <div
                          class="flex items-center gap-3 text-ink/50 text-sm"
                        >
                          <div
                            class="w-5 h-5 flex items-center justify-center bg-sand/40 shrink-0"
                          >
                            <app-icon name="close" [size]="11" />
                          </div>
                          {{ exc }}
                        </div>
                      }
                    </div>
                  </div>
                </div>
              }

              @if (activeTab() === "itinerary") {
                <div class="grid grid-cols-1 md:grid-cols-5 gap-10">
                  <div class="md:col-span-2 md:sticky md:top-32 self-start">
                    <div class="rounded-[2rem] bg-paper-soft/60 p-6">
                      <app-island-map
                        [stops]="t.stops"
                        [activeCode]="activeStop()"
                      />
                    </div>
                    <p
                      class="text-center text-[10px] uppercase tracking-widest text-ink/35 mt-3"
                    >
                      The Route
                    </p>
                  </div>
                  <div class="md:col-span-3 space-y-2">
                    @for (item of t.itinerary; track item.day; let i = $index) {
                      <button
                        type="button"
                        (mouseenter)="setActiveDay(i, item.stopCode)"
                        (focus)="setActiveDay(i, item.stopCode)"
                        class="w-full text-left flex items-start gap-6 p-5 rounded-2xl transition-colors"
                        [class]="
                          activeDayIndex() === i
                            ? 'bg-paper-soft'
                            : 'hover:bg-paper-soft/60'
                        "
                      >
                        <div class="flex flex-col items-center shrink-0">
                          <div
                            class="w-10 h-10 rounded-full flex items-center justify-center route-code text-xs font-bold transition-colors"
                            [class]="
                              activeDayIndex() === i
                                ? 'bg-vermilion text-paper glow-vermilion'
                                : 'bg-ink text-paper'
                            "
                          >
                            {{ item.stopCode }}
                          </div>
                          @if (!$last) {
                            <div
                              class="w-px h-full bg-sand my-1.5"
                              style="min-height: 1rem;"
                            ></div>
                          }
                        </div>
                        <div class="pb-4">
                          <div class="route-code text-[10px] text-teal mb-1">
                            {{ item.day }}
                          </div>
                          <h3 class="text-lg font-serif mb-2">
                            {{ item.title }}
                          </h3>
                          <ul class="space-y-1.5">
                            @for (act of item.activities; track act) {
                              <li
                                class="flex items-start gap-2.5 text-ink/60 text-sm"
                              >
                                <div
                                  class="w-1 h-1 rounded-full bg-vermilion mt-2 shrink-0"
                                ></div>
                                {{ act }}
                              </li>
                            }
                          </ul>
                        </div>
                      </button>
                    }
                  </div>
                </div>
              }

              @if (activeTab() === "gallery") {
                <div class="space-y-6">
                  <div
                    class="relative aspect-video overflow-hidden bg-ink rounded-[2rem]"
                  >
                    <img
                      [src]="t.gallery[currentImageIndex()]"
                      [alt]="t.title + ' photo ' + (currentImageIndex() + 1)"
                      class="w-full h-full object-contain transition-all duration-500"
                      referrerpolicy="no-referrer"
                    />
                    @if (t.gallery.length > 1) {
                      <button
                        (click)="prevImage()"
                        aria-label="Previous photo"
                        class="absolute left-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full glass flex items-center justify-center transition-all"
                      >
                        <app-icon name="chevron-left" [size]="18" />
                      </button>
                      <button
                        (click)="nextImage()"
                        aria-label="Next photo"
                        class="absolute right-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full glass flex items-center justify-center transition-all"
                      >
                        <app-icon name="chevron-right" [size]="18" />
                      </button>
                      <div
                        class="absolute bottom-5 left-1/2 -translate-x-1/2 route-code text-[10px] bg-ink/80 text-paper px-3 py-1.5 rounded-full"
                      >
                        {{ currentImageIndex() + 1 }} / {{ t.gallery.length }}
                      </div>
                    }
                  </div>
                  @if (t.gallery.length > 1) {
                    <div class="grid grid-cols-5 gap-3">
                      @for (img of t.gallery; track img; let i = $index) {
                        <button
                          (click)="currentImageIndex.set(i)"
                          [attr.aria-label]="'View photo ' + (i + 1)"
                          class="aspect-video overflow-hidden rounded-xl bg-ink transition-all"
                          [class]="
                            currentImageIndex() === i
                              ? 'ring-2 ring-vermilion'
                              : 'opacity-60 hover:opacity-100'
                          "
                        >
                          <img
                            [src]="img"
                            alt=""
                            class="w-full h-full object-contain"
                            referrerpolicy="no-referrer"
                          />
                        </button>
                      }
                    </div>
                  }
                </div>
              }
            </div>
          </div>

          <!-- Sidebar -->
          <div class="space-y-6">
            <div class="glass rounded-[2rem] p-8 sm:p-9 sticky top-28">
              <div class="mb-8">
                <span
                  class="text-[10px] uppercase tracking-widest font-bold text-ink/40"
                  >Starting from, per person</span
                >
                <div class="flex items-end gap-2 mt-2">
                  <span class="route-code text-2xl text-ink/50 mb-1">From</span>
                  <span class="route-code text-5xl text-ink"
                    >&#36;{{ t.price }}</span
                  >
                </div>
                <p class="text-ink/45 text-xs mt-1">{{ t.priceNote }}</p>
              </div>

              <dl class="space-y-3 mb-8 text-sm">
                <div class="flex justify-between py-2 border-b border-sand/60">
                  <dt class="text-ink/50">Departure</dt>
                  <dd class="font-semibold">{{ t.departureTime }}</dd>
                </div>
                <div class="flex justify-between py-2 border-b border-sand/60">
                  <dt class="text-ink/50">Return</dt>
                  <dd class="font-semibold">{{ t.returnTime }}</dd>
                </div>
                <div class="flex justify-between py-2 border-b border-sand/60">
                  <dt class="text-ink/50">Route</dt>
                  <dd class="font-semibold route-code text-xs">
                    {{ routeLabel() }}
                  </dd>
                </div>
              </dl>

              <a
                [routerLink]="['/contact']"
                [queryParams]="{ tour: t.id }"
                class="btn-vermilion w-full"
              >
                Enquire About This Tour
                <app-icon name="arrow-right" [size]="16" />
              </a>
              <p class="text-center text-[11px] text-ink/40 mt-4">
                Opens a short form - we reply within one business day
              </p>
            </div>

            <div class="p-7 rounded-[1.5rem] border border-sand/60 space-y-3">
              <div class="flex items-center gap-2.5 text-teal">
                <app-icon name="shield" [size]="18" />
                <span class="font-semibold text-sm">Planned With Care</span>
              </div>
              <p class="text-xs text-ink/55 leading-relaxed">
                Every itinerary is routed and vetted by our Colombo team. Dates,
                hotels and pace can all be adjusted before you commit to
                anything.
              </p>
            </div>
          </div>
        </div>
      </div>
    } @else {
      <div
        class="min-h-[60vh] flex flex-col items-center justify-center text-center px-6 py-32"
      >
        <span class="route-code text-vermilion text-sm mb-4"
          >404 - ROUTE NOT FOUND</span
        >
        <h1 class="text-4xl font-serif mb-4">We couldn't find that tour</h1>
        <p class="text-ink/55 mb-8 max-w-md">
          It may have been renamed or retired. Have a look at the full route
          list instead.
        </p>
        <a routerLink="/tours" class="btn-vermilion">View All Tours</a>
      </div>
    }
  `,
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
})
export class TourDetail {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private dataService = inject(DataService);

  tabs = [
    { id: "overview" as const, label: "Overview" },
    { id: "itinerary" as const, label: "Itinerary" },
    { id: "gallery" as const, label: "Gallery" },
  ];

  tour = signal<Tour | undefined>(undefined);
  activeTab = signal<"overview" | "itinerary" | "gallery">("overview");
  currentImageIndex = signal(0);
  activeStop = signal<string | undefined>(undefined);
  activeDayIndex = signal<number | undefined>(undefined);
  saved = signal(false);

  constructor() {
    this.route.params.subscribe((params) => {
      const found = this.dataService.getTourById(params["id"]);
      this.tour.set(found);
      this.currentImageIndex.set(0);
      this.activeTab.set("overview");
      this.activeStop.set(found?.stops[0]?.code);
      this.activeDayIndex.set(undefined);
    });
  }

  setActiveDay(index: number, stopCode: string) {
    this.activeDayIndex.set(index);
    this.activeStop.set(stopCode);
  }

  routeLabel() {
    const r = this.tour()?.route ?? [];
    return r.join(" → ");
  }

  nextImage() {
    const gallery = this.tour()?.gallery;
    if (gallery?.length)
      this.currentImageIndex.update((i) => (i + 1) % gallery.length);
  }

  prevImage() {
    const gallery = this.tour()?.gallery;
    if (gallery?.length)
      this.currentImageIndex.update(
        (i) => (i - 1 + gallery.length) % gallery.length,
      );
  }

  share() {
    const t = this.tour();
    if (!t) return;
    if (typeof navigator !== "undefined" && navigator.share) {
      navigator
        .share({ title: t.title, text: t.tagline, url: window.location.href })
        .catch(() => {});
    }
  }
}
